// ElevenLabs cast — partner-voice synthesis wrapping for the Run Lines
// Player. Mirrors server/routes/cast.py.

import { API_BASE } from "./client";

export interface LibraryVoice {
  voice_id: string;
  name: string;
  gender: "female" | "male" | "neutral";
  desc: string;
}

export interface CastLineEntry {
  voice_id: string;
  text_hash: string;
  status: "ready" | "stale" | "error";
  audio_url: string;       // absolute after _absolutize
}

export interface CastEnvelope {
  project_id: string;
  characters: string[];
  actor_character: string | null;
  assignments: Record<string, string>;       // character → voice_id
  manifest: Record<string, CastLineEntry>;   // line_id (str) → entry
  voices_library: LibraryVoice[];
}

export interface CastSynthState {
  status: "not_started" | "starting" | "running" | "complete" | "error";
  total: number;
  done: number;
  error?: string;
  updated_at?: string;
}

function absUrl(url: string): string {
  return url.startsWith("http") ? url : `${API_BASE}${url}`;
}

export async function getCast(projectId: string): Promise<CastEnvelope> {
  const res = await fetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/cast`);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const env: CastEnvelope = await res.json();
  // Absolutize manifest audio URLs for direct <audio>.src use
  for (const lid of Object.keys(env.manifest)) {
    env.manifest[lid] = { ...env.manifest[lid], audio_url: absUrl(env.manifest[lid].audio_url) };
  }
  return env;
}

export async function autoAssign(projectId: string, actorRole: string): Promise<{ assignments: Record<string, string> }> {
  const res = await fetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/cast/auto-assign`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ actor_role: actorRole }),
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json();
}

export async function startSynthesize(projectId: string, actorRole: string): Promise<{ status: string }> {
  const res = await fetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/cast/synthesize`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ actor_role: actorRole }),
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json();
}

export async function getSynthStatus(projectId: string): Promise<CastSynthState> {
  const res = await fetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/cast/synthesize`);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json();
}

export function castAudioUrl(projectId: string, lineId: number): string {
  return `${API_BASE}/projects/${encodeURIComponent(projectId)}/cast/audio/${lineId}`;
}
